/**
 * Plan, trial and price display helpers shared by the signup, Billing,
 * Organisation and platform pages. Plan names and prices always come from the
 * API; these only format them.
 */

/**
 * A trial is a status, not a plan: a tenant on trial carries the plan it will
 * convert to. Tenants from before that still have Plan = "Trial", which the
 * API treats as a trial of Professional.
 */
export const LEGACY_TRIAL_PLAN = 'Trial';

/** How a legacy "Trial" plan reads everywhere it's shown. */
export const LEGACY_TRIAL_LABEL = 'Professional (legacy trial)';

/** The plan to show; a legacy "Trial" plan reads as the Professional trial it is. */
export function planLabel(plan: string | null | undefined): string {
  if (!plan) return '—';
  return plan === LEGACY_TRIAL_PLAN ? LEGACY_TRIAL_LABEL : plan;
}

/**
 * "30 Sept 2026". A trial ends at 23:59:59 UTC on its last day, so it's shown
 * in UTC: in local time that instant is already the next day east of Greenwich
 * (a UK summer evening included).
 */
export function formatTrialEnd(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The status to show: "Trial" for a legacy trial tenant still marked Active. */
export function tenantStatusLabel(status: string, plan?: string | null): string {
  return plan === LEGACY_TRIAL_PLAN && status === 'Active' ? 'Trial' : status;
}

export function intervalLabel(interval?: string | null): string {
  return interval === 'year' ? 'Yearly' : 'Monthly';
}

/** £10, £9.99, £8.33 — whole amounts drop the pence. */
export function formatPence(pence: number, currency = 'gbp'): string {
  const amount = pence / 100;
  const whole = pence % 100 === 0;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(whole ? 0 : 2)}`;
  }
}
